<template>
  <v-container>
    <h1 class="text-center">📅 Stream-Plan</h1>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">Grunddaten</v-card-title>
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            :items="channels"
            item-text="name"
            item-value="id"
            label="Kanal für die Veröffentlichung"
            prepend-icon="mdi-pound"
            outlined
            v-model="targetChannelId"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="title" label="Titel" prepend-icon="mdi-format-title" outlined></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="description"
            label="Beschreibung (optional)"
            prepend-icon="mdi-text-long"
            outlined
            rows="2"
          ></v-textarea>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">🗓️ Wochenplan</v-card-title>
      <v-alert type="info" text dense border="left" class="mb-4">
        Gültig für die Woche vom <strong>{{ weekStart }}</strong> bis <strong>{{ weekEnd }}</strong> –
        das Datum wird beim Veröffentlichen automatisch für die jeweils aktuelle Woche ergänzt.
      </v-alert>
      <v-row>
        <v-col cols="12" sm="6" md="4" v-for="(day, index) in weekdays" :key="day.key">
          <v-text-field
            v-model="schedule[day.key]"
            :label="`${day.label}, ${weekDates[index]}`"
            outlined
            dense
            placeholder="z.B. 20:00 Uhr – Valorant (oder „Frei“)"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">🖼️ Bilder</v-card-title>
      <v-row>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="logoFile"
            label="Logo auswählen"
            prepend-icon="mdi-image"
            accept="image/*"
            outlined
            truncate-length="20"
            hint="Kleines Logo/Icon, erscheint als Miniaturbild im Discord-Post"
            persistent-hint
          ></v-file-input>
          <div v-if="existingLogoPath" class="d-flex flex-wrap align-center mt-3">
            <v-img
              :src="logoPreviewUrl"
              max-width="120"
              max-height="120"
              class="rounded-lg elevation-2"
            ></v-img>
            <v-btn text small color="red" class="ml-3" @click="removeLogo">
              <v-icon left small>mdi-delete</v-icon>
              Entfernen
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="bannerFile"
            label="Banner auswählen"
            prepend-icon="mdi-panorama"
            accept="image/*"
            outlined
            truncate-length="20"
            hint="Wird als Hintergrundbild hinter dem Wochenplan verwendet"
            persistent-hint
          ></v-file-input>
          <div class="text-caption grey--text mt-1">
            Empfohlene Größe: <strong>{{ recommendedBannerWidth }} × {{ recommendedBannerHeight }} px</strong>
            (Seitenverhältnis {{ recommendedBannerRatio }}:1, auch ein Vielfaches davon funktioniert) –
            damit füllt das Banner den Rahmen ohne Ränder komplett aus.
          </div>
          <v-switch
            v-model="bannerCover"
            label="Bild zuschneiden, um Rahmen vollständig auszufüllen"
            hint="Aus: komplettes Banner sichtbar, ggf. mit Rand. Ein: randlos, Bild wird passend beschnitten."
            persistent-hint
            dense
            class="mt-2"
          ></v-switch>
          <div v-if="existingBannerPath" class="d-flex flex-wrap align-center mt-3">
            <v-img
              :src="bannerPreviewUrl"
              max-width="320"
              max-height="120"
              class="rounded-lg elevation-2"
              style="width: 100%;"
            ></v-img>
            <v-btn text small color="red" class="mt-2" @click="removeBanner">
              <v-icon left small>mdi-delete</v-icon>
              Entfernen
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">🔁 Automatisierung</v-card-title>
      <v-switch
        v-model="autoRepost"
        label="Diesen Stream-Plan jede Woche automatisch neu veröffentlichen (jeden Montag, 08:00 Uhr)"
        hint="Der Wochenplan bleibt unverändert und wird einfach jede Woche mit aktuellem Datum neu gepostet."
        persistent-hint
        dense
      ></v-switch>
    </v-card>

    <v-row>
      <v-col cols="12" class="text-center">
        <v-btn
          color="primary"
          large
          :block="$vuetify.breakpoint.smAndDown"
          :loading="publishing"
          @click="publish"
        >
          <v-icon left>mdi-send</v-icon>
          Veröffentlichen
        </v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="publishResult.show"
      timeout="5000"
      :color="publishResult.success ? 'green' : 'red'"
    >
      {{ publishResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

// Muss synchron zu RECOMMENDED_BANNER_WIDTH/HEIGHT in
// Discord_Bot/src/utils/streamPlanImage.js gehalten werden.
const RECOMMENDED_BANNER_WIDTH = 1120;
const RECOMMENDED_BANNER_HEIGHT = 88;

export default {
  data() {
    return {
      targetChannelId: null,
      title: '',
      description: '',
      schedule: {
        monday: 'Frei',
        tuesday: 'Frei',
        wednesday: 'Frei',
        thursday: 'Frei',
        friday: 'Frei',
        saturday: 'Frei',
        sunday: 'Frei',
      },
      weekdays: [
        { key: 'monday', label: 'Montag' },
        { key: 'tuesday', label: 'Dienstag' },
        { key: 'wednesday', label: 'Mittwoch' },
        { key: 'thursday', label: 'Donnerstag' },
        { key: 'friday', label: 'Freitag' },
        { key: 'saturday', label: 'Samstag' },
        { key: 'sunday', label: 'Sonntag' },
      ],
      channels: [],
      logoFile: null,
      existingLogoPath: null,
      bannerFile: null,
      existingBannerPath: null,
      bannerCover: false,
      removeLogoFlag: false,
      removeBannerFlag: false,
      autoRepost: false,
      publishing: false,
      publishResult: {
        show: false,
        success: false,
        message: '',
      },
    };
  },
  computed: {
    logoPreviewUrl() {
      return `${process.env.VUE_APP_BACKENDSERVICE_BASEURL}img/${this.existingLogoPath}`;
    },
    bannerPreviewUrl() {
      return `${process.env.VUE_APP_BACKENDSERVICE_BASEURL}img/${this.existingBannerPath}`;
    },
    recommendedBannerWidth() {
      return RECOMMENDED_BANNER_WIDTH;
    },
    recommendedBannerHeight() {
      return RECOMMENDED_BANNER_HEIGHT;
    },
    recommendedBannerRatio() {
      return (RECOMMENDED_BANNER_WIDTH / RECOMMENDED_BANNER_HEIGHT).toFixed(1);
    },
    weekDateObjects() {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sonntag ... 6 = Samstag
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday);
      const mondayYear = monday.getFullYear();
      const mondayMonth = monday.getMonth();
      const mondayDay = monday.getDate();
      return Array.from({ length: 7 }, (_, i) => new Date(mondayYear, mondayMonth, mondayDay + i));
    },
    weekDates() {
      const pad = (n) => String(n).padStart(2, '0');
      return this.weekDateObjects.map(
        (date) => `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`,
      );
    },
    weekStart() {
      return this.weekDates[0];
    },
    weekEnd() {
      return this.weekDates[6];
    },
  },
  watch: {
    logoFile(file) {
      if (file) this.removeLogoFlag = false;
    },
    bannerFile(file) {
      if (file) this.removeBannerFlag = false;
    },
  },
  mounted() {
    BackendService.loadStreamPlanSettings().then((data) => {
      const s = data.data.settings;
      this.targetChannelId = s.target_channel_id;
      this.title = s.title;
      this.description = s.description;
      this.existingLogoPath = s.logo_path;
      this.existingBannerPath = s.banner_path;
      this.bannerCover = s.banner_fit_mode === 'cover';
      this.autoRepost = Number(s.auto_repost) === 1;
      this.weekdays.forEach((day) => {
        this.schedule[day.key] = s[`${day.key}_schedule`] || 'Frei';
      });
      this.channels = data.data.channels;
    });
  },
  methods: {
    removeLogo() {
      this.logoFile = null;
      this.existingLogoPath = null;
      this.removeLogoFlag = true;
    },
    removeBanner() {
      this.bannerFile = null;
      this.existingBannerPath = null;
      this.removeBannerFlag = true;
    },
    publish() {
      this.publishing = true;
      BackendService.saveStreamPlanSettings({
        target_channel_id: this.targetChannelId,
        title: this.title,
        description: this.description,
        logo: this.logoFile || undefined,
        remove_logo: this.removeLogoFlag ? '1' : '0',
        banner: this.bannerFile || undefined,
        remove_banner: this.removeBannerFlag ? '1' : '0',
        banner_fit_mode: this.bannerCover ? 'cover' : 'contain',
        auto_repost: this.autoRepost ? '1' : '0',
        monday_schedule: this.schedule.monday,
        tuesday_schedule: this.schedule.tuesday,
        wednesday_schedule: this.schedule.wednesday,
        thursday_schedule: this.schedule.thursday,
        friday_schedule: this.schedule.friday,
        saturday_schedule: this.schedule.saturday,
        sunday_schedule: this.schedule.sunday,
      }).then((data) => {
        this.existingLogoPath = data.data.logoPath;
        this.existingBannerPath = data.data.bannerPath;
        return BackendService.publishStreamPlan();
      }).then((data) => {
        this.publishResult = {
          show: true,
          success: data.data.success,
          message: data.data.success
            ? 'Stream-Plan wurde veröffentlicht!'
            : `Fehlgeschlagen: ${data.data.error}`,
        };
      }).catch((error) => {
        const message = error.response && error.response.data === 'streamer_not_connected_with_dc_bot'
          ? 'Streamer benutzt aktuell den DC-Bot nicht!'
          : 'Fehlgeschlagen: Verbindung zum Server nicht möglich.';
        this.publishResult = { show: true, success: false, message };
      })
        .finally(() => {
          this.publishing = false;
        });
    },
  },
};
</script>
